// src/components/marketing/MarketingTab.jsx
// 마케팅 탭: 즉시 실행형 재방문 홍보

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { MARKETING_PROMO_TYPES, sendMarketingMessage } from '../../services/messageTemplates';

// ─── 고정 홍보 템플릿 ───
// {이름}·{할인혜택}만 치환, [기간 입력]은 발송 전 수동 수정
const DISCOUNT_TEMPLATE =
`안녕하세요 {이름} 고객님 😊

지난 방문 감사드립니다!

👉 다음 예약 시 {할인혜택}
(예: 20% 할인 / 3만원 할인 / 바베큐 무료)

📅 적용 기간: [기간 입력]

편하게 문의 주세요 🙂`;

function buildDiscountMessage(name, discount) {
  return DISCOUNT_TEMPLATE
    .replace(/{이름}/g, name || '고객')
    .replace(/{할인혜택}/g, discount?.trim() || '특별 혜택');
}

// ─── 유틸 ───
function getDaysColor(days) {
  if (days >= 120) return 'var(--red)';
  if (days >= 60) return 'var(--amber)';
  return 'var(--text-secondary)';
}

function getCurrentSeason() {
  const m = new Date().getMonth() + 1;
  if (m >= 3 && m <= 5) return 'spring';
  if (m >= 6 && m <= 8) return 'summer';
  if (m >= 9 && m <= 11) return 'fall';
  return 'winter';
}

function getSeasonLabel() {
  const m = new Date().getMonth() + 1;
  if (m >= 3 && m <= 5) return '봄';
  if (m >= 6 && m <= 8) return '여름';
  if (m >= 9 && m <= 11) return '가을';
  return '겨울';
}

const SEASON_PROMO_MAP = {
  spring: 'earlybird',
  summer: 'pool',
  fall:   'promo_bbq',
  winter: 'firewood',
};

// ─── 추천 카드 생성 ───
function buildRecommendCards(candidates) {
  const season     = getCurrentSeason();
  const seasonLabel = getSeasonLabel();
  const month      = new Date().getMonth() + 1;
  const seasonPromoId = SEASON_PROMO_MAP[season];

  const groupCandidates  = candidates.filter(c => c.badges?.includes('단체') || (c.guests || 0) >= 8);
  const regularCandidates = candidates.filter(c => c.badges?.includes('단골') || c.visits >= 2);
  const familyCandidates  = candidates.filter(c => { const g = c.guests || 0; return g >= 2 && g < 8; });

  const cards = [];

  const seasonPromo = MARKETING_PROMO_TYPES.find(p => p.id === seasonPromoId);
  if (seasonPromo) {
    cards.push({
      promoId: seasonPromoId,
      title: `${seasonLabel} 시즌 ${seasonPromo.name}`,
      icon: seasonPromo.icon,
      reason: '지금 보내면 반응 좋은 시기',
      targets: candidates,
    });
  }

  if (groupCandidates.length > 0) {
    cards.push({
      promoId: 'group',
      title: '단체 특가',
      icon: '👥',
      reason: '단체로 방문했던 고객',
      targets: groupCandidates,
    });
  }

  if (regularCandidates.length > 0) {
    cards.push({
      promoId: 'stay',
      title: '단골 할인',
      icon: '🏷️',
      reason: '자주 방문했던 단골 고객',
      targets: regularCandidates,
    });
  }

  if (familyCandidates.length > 0) {
    cards.push({
      promoId: 'family',
      title: '가족 특가',
      icon: '👨‍👩‍👧',
      reason: '가족으로 방문했던 고객',
      targets: familyCandidates,
    });
  }

  return cards.slice(0, 4);
}

// ─── 발송 공통 로직 ───
async function executeSend({ promo, targets, messageTemplate, logMessage, updateLastDiscount }) {
  let success = 0;
  for (const customer of targets) {
    // 편집된 템플릿에서 {이름}만 각 고객명으로 치환
    const msg = messageTemplate.replace(/{이름}/g, customer.name || '고객');

    // 2) 발송 (핵심 — 실패 시 해당 고객 건너뜀)
    try {
      await sendMarketingMessage({ phone: customer.phone, messageText: msg });
    } catch (e) {
      console.error(`[마케팅 발송 실패] ${customer.name} (${customer.phone}):`, e?.message || e);
      continue; // 이 고객 건너뛰고 다음으로
    }

    success++;

    // 3) 로그 저장 / 고객 업데이트 (부수 처리 — 실패해도 발송 카운트에 영향 없음)
    try {
      await logMessage({
        customerId: customer.id,
        customerName: customer.name,
        phone: customer.phone,
        templateId: 'revisit',
        templateName: `재방문 홍보 — ${promo.name}`,
        roomId: customer.roomId || null,
        messageText: msg,
      });
    } catch (e) {
      console.warn(`[마케팅 로그 저장 실패] ${customer.name}:`, e?.message || e);
    }

    try {
      await updateLastDiscount(customer.id);
    } catch (e) {
      console.warn(`[마케팅 고객 업데이트 실패] ${customer.name}:`, e?.message || e);
    }

    await new Promise(r => setTimeout(r, 150));
  }
  return success;
}

export default function MarketingTab() {
  const { getDaysSinceVisit, getRevisitCandidates, logMessage, updateLastDiscount, showToast, state } = useApp();
  const { user } = state;

  const candidates     = getRevisitCandidates();
  const recommendCards = useMemo(() => buildRecommendCards(candidates), [candidates]);
  const recommendPromoIds = useMemo(() => new Set(recommendCards.map(c => c.promoId)), [recommendCards]);

  // ── 바텀 시트 상태 ──
  const [editOpen, setEditOpen]           = useState(false);
  const [editPromo, setEditPromo]         = useState(null);
  const [editTargets, setEditTargets]     = useState([]);
  const [discountInput, setDiscountInput] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [editSending, setEditSending]     = useState(false);
  const [showAllCustomers, setShowAllCustomers] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);

  const CUSTOMER_PREVIEW = 4;
  const customerListRef = useRef(null);
  const discountRef = useRef(null);

  // discountInput 변경 시 메시지 자동 동기화 (직접 수정도 가능)
  useEffect(() => {
    setCustomMessage(
      DISCOUNT_TEMPLATE.replace(/{할인혜택}/g, discountInput.trim() || '특별 혜택')
    );
  }, [discountInput]);

  // ── 시트 열기 ──
  const openSheet = useCallback((promoId, targets) => {
    const promo = MARKETING_PROMO_TYPES.find(p => p.id === promoId);
    if (!promo || targets.length === 0) return;
    setEditPromo(promo);
    setEditTargets(targets);
    setDiscountInput('');
    setShowAllCustomers(false);
    setEditOpen(true);
    // 시트 열리면 할인 입력창에 포커스
    setTimeout(() => discountRef.current?.focus(), 120);
  }, []);

  const closeEdit = useCallback(() => {
    setEditOpen(false);
    setEditPromo(null);
    setEditTargets([]);
    setDiscountInput('');
    setCustomMessage('');
    setShowAllCustomers(false);
  }, []);

  // ── 발송 ──
  async function handleEditSend() {
    if (user?.isAnonymous) {
      showToast('체험 계정에서는 실제 문자를 발송할 수 없습니다.\n회원가입 후 이용해주세요.', 'error');
      return;
    }
    if (!editPromo || editTargets.length === 0 || !discountInput.trim()) return;
    setEditSending(true);
    const success = await executeSend({
      promo: editPromo, targets: editTargets,
      messageTemplate: customMessage, logMessage, updateLastDiscount,
    });
    setEditSending(false);
    closeEdit();
    if (success > 0) {
      showToast(`재방문 홍보 메시지 ${success}건 발송 완료!`);
    } else {
      showToast('발송에 실패했습니다. 콘솔에서 오류를 확인하세요.', 'error');
    }
  }

  // ── 고객 토글 (바텀 시트 내) ──
  function toggleTarget(customer) {
    setEditTargets(prev =>
      prev.find(c => c.id === customer.id)
        ? prev.filter(c => c.id !== customer.id)
        : [...prev, customer]
    );
  }

  // ── 빈 상태 ──
  if (candidates.length === 0) {
    return (
      <div className="tab-content">
        <div className="empty-state" style={{ paddingTop: 80 }}>
          <div className="icon">🎉</div>
          <p style={{ fontWeight: 700, fontSize: 16 }}>재방문 홍보 대상이 없습니다</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>모든 고객이 최근 60일 내 방문했습니다</p>
        </div>
      </div>
    );
  }

  // ── 고객 토글 (바텀 시트 내) ──
  function toggleTarget(customer) {
    setEditTargets(prev =>
      prev.find(c => c.id === customer.id)
        ? prev.filter(c => c.id !== customer.id)
        : [...prev, customer]
    );
  }

  const atRiskCount = candidates.filter(c => getDaysSinceVisit(c.lastVisit) >= 120).length;

  const filteredCustomers = useMemo(() => {
    if (!selectedFilter) return [];
    if (selectedFilter === 'risk') return candidates.filter(c => getDaysSinceVisit(c.lastVisit) >= 120);
    if (selectedFilter === 'normal') return candidates.filter(c => getDaysSinceVisit(c.lastVisit) < 120);
    return candidates; // 'all'
  }, [selectedFilter, candidates, getDaysSinceVisit]);

  function handleFilterClick(filter) {
    const next = selectedFilter === filter ? null : filter;
    setSelectedFilter(next);
    if (next) {
      setTimeout(() => customerListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    }
  }

  return (
    <>
      <div className="tab-content">

        {/* ── 🔥 컴팩트 배너 ── */}
        <div style={{
          background: 'linear-gradient(135deg, #b45309 0%, #d97706 100%)',
          borderRadius: 16,
          padding: '14px 16px',
          marginBottom: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          boxShadow: '0 3px 14px rgba(180,83,9,0.28)',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'white', marginBottom: 2 }}>
              🔥 지금 연락하면 다시 올 고객 {candidates.length}명
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>
              {atRiskCount > 0
                ? <><span style={{ color: '#fde68a', fontWeight: 700 }}>👉 오래 안 온 고객 {atRiskCount}명</span> 포함</>
                : '👉 지금 메시지 보내면 재방문 확률 높아요'}
            </div>
          </div>
          <button
            onClick={() => openSheet(SEASON_PROMO_MAP[getCurrentSeason()], candidates)}
            style={{
              flexShrink: 0,
              background: 'white', border: 'none',
              borderRadius: 12, padding: '10px 16px',
              fontSize: 13, fontWeight: 800, color: '#b45309',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            메시지 보내기
          </button>
        </div>

        {/* ── 추천 카드 2열 그리드 ── */}
        <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.06em' }}>
          👉 이 고객들에게 이렇게 보내보세요
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 10,
          marginBottom: 16,
        }}>
          {recommendCards.map((card, i) => {
            return (
              <div
                key={i}
                style={{
                  background: 'white',
                  border: '1.5px solid var(--border)',
                  borderRadius: 16,
                  padding: '14px 12px 12px',
                  display: 'flex', flexDirection: 'column',
                }}
              >
                {/* 카드 정보 */}
                <div style={{ fontSize: 22, marginBottom: 5 }}>{card.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)', marginBottom: 2, lineHeight: 1.3 }}>
                  {card.title}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4, marginBottom: 10, flex: 1 }}>
                  예약 가능성 높은 고객 · <strong style={{ color: 'var(--amber)' }}>{card.targets.length}명</strong>
                </div>

                {/* 할인 입력 후 발송 */}
                <button
                  onClick={() => openSheet(card.promoId, card.targets)}
                  style={{
                    width: '100%',
                    background: 'var(--green)',
                    border: 'none', borderRadius: 10,
                    padding: '10px 0',
                    fontSize: 12, fontWeight: 800,
                    color: 'white', cursor: 'pointer',
                    transition: 'all 0.12s',
                  }}
                >
                  ✏️ 할인 혜택 입력 후 발송
                </button>
              </div>
            );
          })}
        </div>

        {/* ── 홍보 유형 칩 ── */}
        <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.06em' }}>
          이런 메시지도 보내보세요
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
          {MARKETING_PROMO_TYPES.filter(promo => !recommendPromoIds.has(promo.id)).map(promo => (
            <button
              key={promo.id}
              onClick={() => openSheet(promo.id, candidates)}
              style={{
                padding: '7px 13px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: '1.5px solid var(--border)',
                background: 'white', color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              {promo.icon} {promo.name}
            </button>
          ))}
        </div>

        {/* ── 현황 미니 통계 (클릭 시 고객 리스트 필터) ── */}
        <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.06em' }}>
          대상 고객 확인
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 4 }}>
          {[
            { key: 'all',    label: '👉 지금 보내야 할 고객', value: candidates.length,              color: 'var(--amber)', borderColor: '#f59e0b' },
            { key: 'risk',   label: '놓치기 쉬운 고객',  value: atRiskCount,                    color: 'var(--red)',   borderColor: '#dc2626' },
            { key: 'normal', label: '일반 고객',          value: candidates.length - atRiskCount, color: 'var(--text-secondary)', borderColor: '#6b7280' },
          ].map(({ key, label, value, color, borderColor }) => {
            const isActive = selectedFilter === key;
            return (
              <div
                key={key}
                onClick={() => handleFilterClick(key)}
                style={{
                  background: isActive ? '#fffbeb' : 'white',
                  borderRadius: 12,
                  border: `${isActive ? 2 : 1}px solid ${isActive ? borderColor : 'var(--border)'}`,
                  padding: '10px 8px', textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.14s',
                  boxShadow: isActive ? `0 0 0 3px ${borderColor}22` : 'none',
                }}
              >
                <div style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
                <div style={{ fontSize: 11, color: isActive ? borderColor : 'var(--text-muted)', marginTop: 3, fontWeight: isActive ? 700 : 400 }}>
                  {label}
                </div>
                {isActive && (
                  <div style={{ fontSize: 9, color: borderColor, marginTop: 3, fontWeight: 700 }}>▲ 목록 보기</div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── 필터된 고객 리스트 ── */}
        {selectedFilter && (
          <div ref={customerListRef} style={{ marginTop: 12, paddingBottom: 8 }}>
            <div style={{
              background: 'white', borderRadius: 14,
              border: '1.5px solid var(--border)',
              overflow: 'hidden',
            }}>
              {/* 리스트 헤더 */}
              <div style={{
                padding: '12px 16px',
                background: '#fafafa',
                borderBottom: '1px solid var(--border-light)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {{ all: '👉 지금 보내야 할', risk: '놓치기 쉬운', normal: '일반' }[selectedFilter]} 고객
                  <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>
                    {filteredCustomers.length}명
                  </span>
                </span>
                <button
                  onClick={() => openSheet(SEASON_PROMO_MAP[getCurrentSeason()], filteredCustomers)}
                  disabled={filteredCustomers.length === 0}
                  style={{
                    fontSize: 11, fontWeight: 700, padding: '5px 12px',
                    borderRadius: 8, border: 'none',
                    background: filteredCustomers.length > 0 ? 'var(--green)' : 'var(--border)',
                    color: 'white', cursor: filteredCustomers.length > 0 ? 'pointer' : 'default',
                  }}
                >
                  전체 문자 보내기
                </button>
              </div>

              {/* 고객 목록 */}
              {filteredCustomers.length === 0 ? (
                <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  해당 고객이 없습니다
                </div>
              ) : (
                filteredCustomers.map((customer, idx) => {
                  const daysSince = getDaysSinceVisit(customer.lastVisit);
                  const isAtRisk = daysSince >= 120;
                  return (
                    <div
                      key={customer.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '11px 16px',
                        borderTop: idx > 0 ? '1px solid var(--border-light)' : 'none',
                      }}
                    >
                      {/* 아바타 */}
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                        background: isAtRisk ? '#fef2f2' : 'var(--amber-light)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: 14,
                        color: isAtRisk ? 'var(--red)' : '#92400e',
                      }}>
                        {customer.name[0]}
                      </div>

                      {/* 정보 */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{customer.name}</span>
                          {isAtRisk && (
                            <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 6, background: '#fef2f2', color: 'var(--red)', fontWeight: 600 }}>
                              이탈위험
                            </span>
                          )}
                          {customer.badges?.includes('단골') && (
                            <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 6, background: 'var(--green-light)', color: 'var(--green-dark)', fontWeight: 600 }}>
                              단골
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {customer.roomName || '객실 미지정'}
                          {customer.guests ? ` · ${customer.guests}명` : ''}
                        </div>
                      </div>

                      {/* 방문일 + 개별 발송 */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: getDaysColor(daysSince) }}>{daysSince}일 전</div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>마지막 방문</div>
                        </div>
                        <button
                          onClick={() => openSheet(SEASON_PROMO_MAP[getCurrentSeason()], [customer])}
                          style={{
                            fontSize: 11, fontWeight: 700, padding: '5px 10px',
                            borderRadius: 8, border: '1px solid var(--border)',
                            background: 'white', color: 'var(--text-secondary)',
                            cursor: 'pointer', whiteSpace: 'nowrap',
                          }}
                        >
                          👉 바로 보내기
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

      </div>

      {/* ════ 바텀 시트 (내용 수정 모드) ════ */}
      {editOpen && (
        <>
          {/* 배경 딤 */}
          <div
            onClick={closeEdit}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.45)',
              zIndex: 300,
            }}
          />

          {/* 시트 */}
          <div style={{
            position: 'fixed',
            bottom: 'var(--tabbar-h)',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: 480,
            background: 'white',
            borderRadius: '22px 22px 0 0',
            zIndex: 301,
            maxHeight: '82dvh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 -4px 30px rgba(0,0,0,0.18)',
          }}>

            {/* 드래그 핸들 */}
            <div style={{ padding: '12px 0 6px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
              <div style={{ width: 38, height: 4, borderRadius: 2, background: '#e5e7eb' }} />
            </div>

            {/* 헤더 */}
            <div style={{
              padding: '4px 20px 14px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
              borderBottom: '1px solid var(--border-light)',
            }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 2 }}>
                  {editPromo?.icon} {editPromo?.name} 메시지 수정
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {editTargets.length}명에게 개인화 발송됩니다
                </div>
              </div>
              <button
                onClick={closeEdit}
                style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: 'var(--bg)', border: 'none',
                  fontSize: 16, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text-muted)',
                }}
              >
                ✕
              </button>
            </div>

            {/* ── 대상 고객 (항상 펼쳐짐) ── */}
            <div style={{ flexShrink: 0, borderBottom: '1px solid var(--border-light)' }}>

              {/* 헤더: 선택 현황 + 전체 선택/해제 */}
              <div style={{
                padding: '10px 20px 8px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                  발송 대상{' '}
                  <span style={{ color: 'var(--green-dark)' }}>{editTargets.length}명</span>
                  <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 4 }}>
                    선택됨
                  </span>
                </span>
                <button
                  onClick={() => {
                    if (editTargets.length === candidates.length) setEditTargets([]);
                    else setEditTargets([...candidates]);
                  }}
                  style={{
                    fontSize: 11, fontWeight: 600, padding: '4px 10px',
                    border: '1px solid var(--border)', borderRadius: 8,
                    background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
                  }}
                >
                  {editTargets.length === candidates.length ? '전체 해제' : `전체 선택 (${candidates.length}명)`}
                </button>
              </div>

              {/* 고객 목록 (항상 표시) */}
              {(() => {
                const visibleList = showAllCustomers
                  ? candidates
                  : candidates.slice(0, CUSTOMER_PREVIEW);
                const hiddenCount = candidates.length - CUSTOMER_PREVIEW;

                return (
                  <>
                    {visibleList.map((customer, idx) => {
                      const isSelected = !!editTargets.find(c => c.id === customer.id);
                      const daysSince  = getDaysSinceVisit(customer.lastVisit);
                      const isAtRisk   = daysSince >= 120;

                      return (
                        <div
                          key={customer.id}
                          onClick={() => toggleTarget(customer)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 20px',
                            borderTop: '1px solid var(--border-light)',
                            background: isSelected ? '#f0fdf4' : 'white',
                            cursor: 'pointer',
                            transition: 'background 0.1s',
                          }}
                        >
                          {/* 체크박스 */}
                          <div style={{
                            width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                            border: `2px solid ${isSelected ? 'var(--green)' : 'var(--border)'}`,
                            background: isSelected ? 'var(--green)' : 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.12s',
                          }}>
                            {isSelected && <span style={{ color: 'white', fontSize: 11, fontWeight: 700 }}>✓</span>}
                          </div>

                          {/* 아바타 */}
                          <div style={{
                            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                            background: isAtRisk ? '#fef2f2' : 'var(--amber-light)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 700, fontSize: 14,
                            color: isAtRisk ? 'var(--red)' : '#92400e',
                          }}>
                            {customer.name[0]}
                          </div>

                          {/* 이름 + 정보 */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                              <span style={{ fontWeight: 700, fontSize: 13 }}>{customer.name}</span>
                              {isAtRisk && (
                                <span style={{
                                  fontSize: 10, padding: '1px 5px', borderRadius: 6,
                                  background: '#fef2f2', color: 'var(--red)', fontWeight: 600,
                                }}>
                                  이탈위험
                                </span>
                              )}
                              {customer.badges?.includes('단골') && (
                                <span style={{
                                  fontSize: 10, padding: '1px 5px', borderRadius: 6,
                                  background: 'var(--green-light)', color: 'var(--green-dark)', fontWeight: 600,
                                }}>
                                  단골
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              재방문 대상 · {customer.roomName || '객실 미지정'}
                            </div>
                          </div>

                          {/* 방문일 */}
                          <div style={{
                            textAlign: 'right', flexShrink: 0,
                          }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: getDaysColor(daysSince) }}>
                              {daysSince}일 전
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>마지막 방문</div>
                          </div>
                        </div>
                      );
                    })}

                    {/* 더보기 / 접기 */}
                    {candidates.length > CUSTOMER_PREVIEW && (
                      <button
                        onClick={() => setShowAllCustomers(v => !v)}
                        style={{
                          width: '100%', background: '#fafafa',
                          border: 'none', borderTop: '1px solid var(--border-light)',
                          padding: '10px 0', fontSize: 12, fontWeight: 600,
                          color: 'var(--text-secondary)', cursor: 'pointer',
                        }}
                      >
                        {showAllCustomers
                          ? '▲ 접기'
                          : `▼ 더보기 (${hiddenCount}명 더)`}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>

            {/* 할인 입력 + 미리보기 (스크롤 영역) */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

              {/* 할인 혜택 입력 */}
              <div style={{ marginBottom: 16 }}>
                <label style={{
                  display: 'block', fontSize: 13, fontWeight: 700,
                  color: 'var(--text-primary)', marginBottom: 8,
                }}>
                  할인 혜택 입력 <span style={{ color: 'var(--red)' }}>*</span>
                </label>
                <input
                  ref={discountRef}
                  className="input"
                  type="text"
                  value={discountInput}
                  onChange={e => setDiscountInput(e.target.value)}
                  placeholder="예: 3만원 할인 / 20% 할인 / 바비큐 무료"
                  style={{ fontSize: 15, fontWeight: 600, padding: '13px 14px' }}
                />
                {!discountInput.trim() && (
                  <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--red)' }}>
                    할인 혜택을 입력해야 발송할 수 있습니다
                  </p>
                )}
              </div>

              {/* 메시지 편집 */}
              <div style={{ marginBottom: 4 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
                }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>
                    메시지 수정
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    · {'{이름}'}은 발송 시 각 고객 이름으로 자동 변경됩니다
                  </span>
                </div>
                <textarea
                  value={customMessage}
                  onChange={e => setCustomMessage(e.target.value)}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'white',
                    border: '1.5px solid var(--border)',
                    borderRadius: 12,
                    padding: '14px 16px',
                    fontSize: 13, lineHeight: 1.8,
                    color: 'var(--text-primary)',
                    fontFamily: 'inherit',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    resize: 'vertical',
                    minHeight: 200,
                    outline: 'none',
                  }}
                />
              </div>

            </div>

            {/* 하단 발송 버튼 */}
            <div style={{
              flexShrink: 0,
              padding: '12px 20px 16px',
              borderTop: '1px solid var(--border-light)',
              background: 'white',
            }}>
              <button
                className="btn btn-green btn-block"
                onClick={handleEditSend}
                disabled={editSending || editTargets.length === 0 || !customMessage.trim()}
                style={{
                  padding: '15px 0', fontSize: 15, fontWeight: 800, borderRadius: 14,
                  opacity: (editTargets.length === 0 || !customMessage.trim()) ? 0.45 : 1,
                  boxShadow: '0 3px 14px rgba(34,197,94,0.3)',
                }}
              >
                {editSending
                  ? <><div className="spinner" /> 발송 중...</>
                  : <>📣 {editTargets.length}명에게 홍보 메시지 발송</>
                }
              </button>
            </div>

          </div>
        </>
      )}
    </>
  );
}
